import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import {
  assistantCapabilities,
  assistantRecords,
  summarizeRecord,
  type AiScope,
} from '@/api/assistant';
import { useAuth } from '@/auth/useAuth';
import { AiStructuredResponse, Badge, Button, FormField, Select, TextInput } from '@/ui';
import { aiScopeLabel, assistantBlockLabel } from '@/ui/vocabulary';
import { AssistantProposal } from './AssistantProposal';
import { GeneralAssistant } from './GeneralAssistant';
import type { RecordScope } from './AssistantScope';
import './assistant.css';

/**
 * The assistant: the record on screen, summarized in one click, and a box to ask anything else.
 *
 * It opens on "Summarize this deal" (or customer, or job) for whatever the page is about, so the
 * common case needs no setup. Picking a different record, which takes a kind, a search and a
 * choice, sits behind "Choose another record" and opens by itself only when the page has no record
 * to offer.
 */
export function AssistantPanel({
  scope,
  defaultType,
  recordKey = '',
}: {
  scope: RecordScope | null;
  defaultType: AiScope;
  /** Changes when the page or selected record changes, resetting the record assistant. */
  recordKey?: string;
}) {
  const { identity } = useAuth();
  const askId = useId();
  const capability = useQuery({
    queryKey: ['assistant', identity, 'capabilities'],
    queryFn: assistantCapabilities,
  });

  return (
    <div className="assistant-panel-shell">
      {capability.error && <p role="alert">{capability.error.message}</p>}
      {capability.isPending && <p role="status">Loading assistant…</p>}
      {capability.data && !capability.data.enabled && (
        <p role="status">The assistant is disabled for this workspace.</p>
      )}
      {capability.data?.enabled && (
        <>
          <RecordAssistantPanel
            key={recordKey}
            scope={scope}
            defaultType={defaultType}
            capabilities={capability.data}
          />
          <section className="assistant-ask" aria-labelledby={askId}>
            <h3 id={askId} className="assistant-panel__heading">
              Ask
            </h3>
            <GeneralAssistant compact provider={capability.data.provider} identity={identity} />
          </section>
        </>
      )}
    </div>
  );
}

type AssistantCapabilities = Awaited<ReturnType<typeof assistantCapabilities>>;

/** What the record is called in a sentence: the page's own word for it, or its kind. */
function nounOf(type: AiScope, id: string, scope: RecordScope | null): string {
  return scope?.id === id && scope.noun !== undefined
    ? scope.noun
    : aiScopeLabel(type).toLowerCase();
}

function RecordAssistantPanel({
  scope,
  defaultType,
  capabilities,
}: {
  scope: RecordScope | null;
  defaultType: AiScope;
  capabilities: AssistantCapabilities;
}) {
  const { identity, can } = useAuth();
  const headingId = useId();
  const chooserId = useId();
  const [type, setType] = useState<AiScope>(scope?.type ?? defaultType);
  const [id, setId] = useState(scope?.id ?? '');
  const [choosing, setChoosing] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  // With no record on the page there is nothing to summarize until one is chosen, so the choice
  // is shown rather than hidden.
  const chooserOpen = scope === null || choosing;
  const options = useQuery({
    queryKey: ['assistant', identity, 'records', type, query, page, customerId],
    queryFn: () => assistantRecords(type, query, page, customerId || undefined),
    enabled: capabilities.enabled && chooserOpen,
  });
  const customers = useQuery({
    queryKey: ['assistant', identity, 'customers', query],
    queryFn: () => assistantRecords('CUSTOMER', query, 0),
    enabled: type === 'EQUIPMENT' && capabilities.enabled && chooserOpen,
  });
  const summary = useMutation({
    mutationFn: (level: 'READ_SUMMARIZE' | 'DRAFT_RECOMMEND' | 'EXECUTE_WITH_APPROVAL') =>
      summarizeRecord({
        scopeType: type,
        recordId: id,
        level,
        ...(type === 'EQUIPMENT' && customerId ? { customerId } : {}),
      }),
  });
  const error = options.error ?? customers.error ?? summary.error;
  const data = summary.data;
  const noun = nounOf(type, id, scope);
  const onThePage = scope !== null && id === scope.id;
  const canPrepareNote = type === 'CUSTOMER' && can('AI_DRAFT_REQUEST') && can('CUSTOMER_WRITE');
  let summarizeLabel = 'Summarize record';
  if (summary.isPending) {
    summarizeLabel = 'Reading record…';
  } else if (id) {
    summarizeLabel = `Summarize this ${noun}`;
  }

  return (
    <section className="assistant-panel" aria-labelledby={headingId}>
      <h3 id={headingId} className="assistant-panel__heading">
        {onThePage ? `This ${noun}` : 'Record'}
      </h3>
      <p>Read a record with source citations. The responsible human decides what happens next.</p>
      {error && <p role="alert">{error.message}</p>}
      <Badge tone="ai">
        {capabilities.provider === 'fake-local'
          ? 'Local demonstration · deterministic summary'
          : capabilities.provider}
      </Badge>
      <div className="assistant-panel__actions">
        <Button
          variant="primary"
          disabled={!id || summary.isPending}
          onClick={() => {
            summary.mutate('READ_SUMMARIZE');
          }}
        >
          {summarizeLabel}
        </Button>
        {can('AI_DRAFT_REQUEST') && (
          <Button
            variant="secondary"
            disabled={!id || summary.isPending}
            onClick={() => {
              summary.mutate('DRAFT_RECOMMEND');
            }}
          >
            Draft internal summary
          </Button>
        )}
        {canPrepareNote && (
          <Button
            variant="secondary"
            disabled={!id || summary.isPending}
            onClick={() => {
              summary.mutate('EXECUTE_WITH_APPROVAL');
            }}
          >
            Prepare internal note for approval
          </Button>
        )}
      </div>
      {scope !== null && (
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={choosing}
          aria-controls={choosing ? chooserId : undefined}
          onClick={() => {
            setChoosing(!choosing);
          }}
        >
          Choose another record
        </Button>
      )}
      {chooserOpen && (
        <div id={chooserId} className="assistant-panel__chooser">
          <FormField label="Kind of record">
            <Select
              value={type}
              disabled={summary.isPending}
              onChange={(e) => {
                setType(e.target.value as AiScope);
                setId('');
                setPage(0);
                summary.reset();
              }}
            >
              {capabilities.scopeTypes.map((t) => (
                <option key={t} value={t}>
                  {aiScopeLabel(t)}
                </option>
              ))}
            </Select>
          </FormField>
          {type !== 'OPPORTUNITY' && type !== 'APPOINTMENT' && (
            <FormField label="Find assistant record">
              <TextInput
                value={query}
                disabled={summary.isPending}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setId('');
                  setPage(0);
                  summary.reset();
                }}
              />
            </FormField>
          )}
          {type === 'EQUIPMENT' && (
            <FormField label="Equipment customer">
              <Select
                value={customerId}
                disabled={summary.isPending}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setId('');
                  summary.reset();
                }}
              >
                <option value="">Choose a customer</option>
                {customers.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
          <FormField label="Assistant record">
            <Select
              value={id}
              required
              disabled={summary.isPending}
              onChange={(e) => {
                setId(e.target.value);
                summary.reset();
              }}
            >
              <option value="">Choose a record</option>
              {scope?.type === type && !options.data?.some((o) => o.id === scope.id) && (
                <option value={scope.id}>Current {aiScopeLabel(type).toLowerCase()} record</option>
              )}
              {options.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormField>
          {options.isFetching && <p role="status">Loading records…</p>}
          {!options.isFetching && options.data?.length === 0 && (
            <p>No matching records. Open a record or adjust your search.</p>
          )}
          {type !== 'EQUIPMENT' && (
            <div className="assistant-panel__pages">
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0 || summary.isPending}
                onClick={() => {
                  setPage(page - 1);
                  setId('');
                  summary.reset();
                }}
              >
                Previous records
              </Button>
              <span>Page {page + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!options.data?.length || summary.isPending}
                onClick={() => {
                  setPage(page + 1);
                  setId('');
                  summary.reset();
                }}
              >
                Next records
              </Button>
            </div>
          )}
        </div>
      )}
      {data?.status === 'BLOCKED' && (
        <div role="alert">
          <p>This response was blocked. Review the source record with the responsible human.</p>
          <ul>
            {data.violations.map((v) => (
              <li key={v.code}>{assistantBlockLabel(v.code)}</li>
            ))}
          </ul>
        </div>
      )}
      {data?.proposedActionId && (
        <AssistantProposal
          key={data.proposedActionId}
          id={data.proposedActionId}
          {...(canPrepareNote
            ? {
                onNewDraft: () => {
                  summary.mutate('EXECUTE_WITH_APPROVAL');
                },
              }
            : {})}
        />
      )}
      {data?.response && (
        <AiStructuredResponse
          response={{
            ...data.response,
            risks: data.response.risks.map((r) => ({
              ...r,
              kind: r.kind === 'escalation' ? 'escalation' : 'risk',
            })),
          }}
        />
      )}
    </section>
  );
}
